import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { IApplication } from 'src/app/model/iapplication';
import { PAGE_NAMES } from 'src/app/service/ansokan-form-builder/application-names';
import {
  APPLICANT_FORM_DEF,
  APPLICATION_FORM_DEF,
  ATTACHMENTS_FORM_DEF,
  DEGREE_ONE_FORM_DEF,
  DEGREE_TWO_FORM_DEF,
  QUALIFICATION_GROUP_FORM_DEF,
  SPECIAL_EDUCATION_FORM_DEF,
} from 'src/app/service/ansokan-form-builder/form-definitions';

@Injectable()
export class AnsokanFormBuilderService {

  private readonly APPLICATION_KEY = 'lleg_application';
  private readonly ALL_PAGES: string[] = [
    PAGE_NAMES.APPLICANT.KEY,
    PAGE_NAMES.DEGREE.KEY,
    PAGE_NAMES.SPECIAL_EDUCATION.KEY,
    PAGE_NAMES.VOCATIONAL_QUALIFICATION.KEY,
    PAGE_NAMES.OTHER_QUALIFICATION.KEY,
    PAGE_NAMES.ATTACHMENTS.KEY,
  ];

  constructor(private fb: UntypedFormBuilder) { }

  public saveToStorage(data: IApplication): void {
    // Do not save attachments due to size concerns.
    if (data.attachments) {
      data.attachments.attachments = [];
    }
    sessionStorage.setItem(this.APPLICATION_KEY, JSON.stringify(data));
  }

  public clearStorage(): void {
    sessionStorage.removeItem(this.APPLICATION_KEY);
  }

  private readStorage(): IApplication {
    const data = sessionStorage.getItem(this.APPLICATION_KEY);
    return JSON.parse(data);
  }

  public buildBaseForm(): UntypedFormGroup {
    const application: IApplication = this.readStorage();
    return this.build([], application);
  }

  public setPages(target: UntypedFormGroup, pages: string[]) {
    // Remove old page groups.
    this.ALL_PAGES.forEach((page) => target.removeControl(page));
    // Build a new form group.
    const data = this.readStorage();
    const base = this.build(pages, data);
    // Add each page group to target.
    pages.forEach((page) => {
      const pageGroup = base.get(page);
      target.setControl(page, pageGroup);
    });
  }

  public buildEmptyForm(): UntypedFormGroup {
    return this.build(this.ALL_PAGES);
  }

  private build(pages: string[], input?: IApplication): UntypedFormGroup {
    const application = this.fb.group(APPLICATION_FORM_DEF(input));
    // Add form groups.
    pages.forEach((page) => {
      const def = this.getGroupDef(page, input);
      application.addControl(page, this.fb.group(def));
    });
    // Add input data.
    if (input) {
      application.patchValue(input, { emitEvent: false });
    }

    return application;
  }

  private getGroupDef(name: string, input?: IApplication): { [key: string]: any } {
    switch (name) {
      case PAGE_NAMES.APPLICANT.KEY:
        return APPLICANT_FORM_DEF;
      case PAGE_NAMES.DEGREE.KEY:
        const def = input && input.degree && input.degree.secondExamina ? DEGREE_TWO_FORM_DEF : DEGREE_ONE_FORM_DEF;
        return def(this.fb);
      case PAGE_NAMES.SPECIAL_EDUCATION.KEY:
        return SPECIAL_EDUCATION_FORM_DEF();
      case PAGE_NAMES.VOCATIONAL_QUALIFICATION.KEY:
        return QUALIFICATION_GROUP_FORM_DEF(this.fb, input ? input.vocationalQualification : null);
      case PAGE_NAMES.OTHER_QUALIFICATION.KEY:
        return QUALIFICATION_GROUP_FORM_DEF(this.fb, input ? input.otherQualification : null);
      case PAGE_NAMES.ATTACHMENTS.KEY:
        return ATTACHMENTS_FORM_DEF();
      default:
        throw new Error('No group def found for ' + name);
    }
  }
}
